import React, { useState, useMemo } from 'react';
import TableBuilder, { ModalButtonBuilder, ToastBuilder, ButtonBuilder } from '../../../../../../../../../GlobalComponents/Builders';
import { capitalizeFirstLetter } from '../../../../../../../../../GlobalComponents/Helpers';
import { buildExcelWorkbook } from '../../../../../../../../../GlobalComponents/Helpers/excel';
import CreatableMultiSelect from '../../../../../../../../../GlobalComponents/Common/Component/CreatableMultiSelect';

// Keep each result table's header pinned while its container scrolls.
const stickyHeaderStyles = {
    headRow: { style: { position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'var(--bs-body-bg, #fff)' } },
};

const downloadReconExcel = async (results, sourceResourceName) => {
    const keyResults = results?.data?.keyResults || [];
    if (!keyResults.length) return;

    const sanitizeSheetName = (name) => {
        // Excel forbids: * ? : \ / [ ]
        const sanitized = name.replace(/[*?:\\/[\]]/g, '_');
        return sanitized.length > 31 ? sanitized.substring(0, 31) : sanitized;
    };

    const sheets = keyResults.map(k => {
        const hasParent = k.hasParent && k.values.some(v => v.parentValue || v.parentName);
        const isTSI = k.isTSI;

        if (isTSI) {
            return {
                sheetName: sanitizeSheetName(k.key),
                columns: [
                    { header: 'Count', width: 10, align: 'center' },
                    { header: 'Board ID', width: 10, align: 'center' },
                    { header: 'Board', width: 25 },
                    { header: 'Type ID', width: 10, align: 'center' },
                    { header: 'Type', width: 25 },
                    { header: 'SubType ID', width: 10, align: 'center' },
                    { header: 'SubType', width: 25 },
                    { header: 'Item ID', width: 10, align: 'center' },
                    { header: 'Item', width: 25 },
                ],
                rows: k.values.map(v => [
                    String(v.count ?? ''),
                    String(v.boardId ?? ''),
                    String(v.boardName ?? ''),
                    String(v.typeId ?? ''),
                    String(v.typeName ?? ''),
                    String(v.subTypeId ?? ''),
                    String(v.subTypeName ?? ''),
                    String(v.itemId ?? ''),
                    String(v.itemName ?? ''),
                ]),
            };
        }

        const columns = [
            { header: 'Count', width: 12, align: 'center' },
            { header: 'Value', width: 15 },
            { header: 'Name', width: 35 },
        ];
        if (hasParent) {
            columns.push({ header: `Parent (${k.parentKey || 'parent'})`, width: 15 });
            columns.push({ header: 'Parent Name', width: 35 });
        }

        const rows = k.values.map(v => {
            const row = [String(v.count ?? ''), String(v.value ?? ''), String(v.name ?? '')];
            if (hasParent) {
                row.push(String(v.parentValue ?? ''));
                row.push(String(v.parentName ?? ''));
            }
            return row;
        });

        return { sheetName: sanitizeSheetName(k.key), columns, rows };
    });

    // Summary sheet first
    const summaryRows = keyResults.map(k => [
        k.key,
        String(k.values.length),
        String(k.values.reduce((sum, v) => sum + (v.count || 0), 0)),
    ]);

    sheets.unshift({
        sheetName: 'Summary',
        columns: [
            { header: 'Key', width: 35 },
            { header: 'Distinct Values', width: 18, align: 'center' },
            { header: 'Total Records', width: 18, align: 'center' },
        ],
        rows: summaryRows,
    });

    await buildExcelWorkbook({
        fileName: `${sourceResourceName}_data_recon.xlsx`,
        sheets,
    });
};

const DataReconModal = ({
    sourceResourceId,
    sourceResourceName,
    handleDMTAction,
    jobKeyMappings,
    bulkSelectedKeys = [],
    dmtStates = [],
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [excludeStatuses, setExcludeStatuses] = useState([]);
    const [result, setResult] = useState(null);   // backend response, held for the in-modal viewer
    const [activeKey, setActiveKey] = useState(null);  // which result tab is shown

    const availableKeys = useMemo(() => {
        if (!jobKeyMappings) return [];
        return jobKeyMappings
            .filter(k =>
                String(k.source_resource_id) === String(sourceResourceId)
                && !k.source_key?.startsWith('_info')
                && !k.source_key?.endsWith('_info')
            )
            .map(k => ({ label: k.source_key, value: k.source_key }))
            .filter((k, i, arr) => arr.findIndex(x => x.value === k.value) === i);
    }, [sourceResourceId, jobKeyMappings]);

    const terminalCategories = ['resolved', 'cancelled'];
    const statusOptions = useMemo(() => {
        if (!dmtStates?.length) return [];
        return dmtStates
            .filter(s => s.type === 'status' && parseInt(s.is_active) === 1)
            .map(s => ({
                value: s.name,
                label: `${capitalizeFirstLetter(s.name)}${terminalCategories.includes(s.category) ? ' ⛔' : ''}`,
            }));
    }, [dmtStates]);

    const terminalStatusNames = useMemo(() => {
        if (!dmtStates?.length) return [];
        return dmtStates
            .filter(s => s.type === 'status' && parseInt(s.is_active) === 1 && terminalCategories.includes(s.category))
            .map(s => s.name);
    }, [dmtStates]);

    const resetForm = () => {
        setIsLoading(false);
        setSelectedKeys(bulkSelectedKeys.length > 0 ? [...bulkSelectedKeys] : []);
        setExcludeStatuses(['finalized']);
        setResult(null);
        setActiveKey(null);
    };

    const keyResults = result?.keyResults || [];
    const canRun = selectedKeys?.length > 0;

    const runRecon = (onSuccess) => {
        if (!canRun) return ToastBuilder('error', 'Select at least one key');
        setIsLoading(true);
        handleDMTAction('data-recon', sourceResourceId, (results) => {
            setIsLoading(false);
            if (results?.data?.success) {
                setResult(results.data);
                setActiveKey(results.data.keyResults?.[0]?.key ?? null);
                onSuccess?.(results);
            } else {
                ToastBuilder('error', results?.data?.message || 'Failed to run data recon');
            }
        }, {
            keys: selectedKeys,
            excludeStatuses: excludeStatuses.length > 0 ? excludeStatuses : undefined,
        });
    };

    // Analyze and show in-modal (no download, no close).
    const handleGenerate = () => runRecon((results) =>
        ToastBuilder('success', `Recon complete — ${results.data.keyResults.length} key(s) analyzed`));

    // Download Excel without closing. Reuse the loaded result if present; else fetch fresh.
    const handleDownload = () => {
        if (result) return downloadReconExcel({ data: result }, sourceResourceName);
        runRecon((results) => downloadReconExcel(results, sourceResourceName));
    };

    // Columns for one key's result table, adapting to TSI / parent / plain shapes.
    const columnsForKey = (k) => {
        if (k.isTSI) {
            return [
                { name: 'Count', selector: (r) => r.count, sortable: true, width: '110px', center: true },
                { name: 'Board', selector: (r) => r.boardName, sortable: true, wrap: true },
                { name: 'Type', selector: (r) => r.typeName, sortable: true, wrap: true },
                { name: 'SubType', selector: (r) => r.subTypeName, sortable: true, wrap: true },
                { name: 'Item', selector: (r) => r.itemName, sortable: true, wrap: true },
            ];
        }
        const cols = [
            { name: 'Count', selector: (r) => r.count, sortable: true, width: '110px', center: true },
            { name: 'Value ID', selector: (r) => (r.value ?? ''), sortable: true, width: '150px' },
            { name: 'Value', selector: (r) => (r.name ?? ''), sortable: true, wrap: true },
        ];
        if (k.hasParent) cols.push({ name: capitalizeFirstLetter(k.parentKey || 'parent'), selector: (r) => (r.parentName || r.parentValue || ''), sortable: true, wrap: true });
        return cols;
    };

    const searchKeysForKey = (k) => k.isTSI
        ? ['boardName', 'typeName', 'subTypeName', 'itemName']
        : ['value', 'name', 'parentName'];

    return (
        <ModalButtonBuilder title='Data Recon' className='btn-outline-danger'
            modalTitle={`${sourceResourceName} Data Recon`}
            style={{ maxWidth: keyResults.length ? '1100px' : '560px' }}
            onClick={resetForm}
            onSubmit={false}
            onCancelLabel='Close'
            body={
                <div>
                    <p className='mb-1' style={{ fontSize: 13 }}>Analyze staged records by counting distinct values for selected keys.</p>
                    <ul className='text-muted ps-3 mb-3' style={{ fontSize: 12 }}>
                        <li>Select from available keys or type custom ones (e.g. <b>TSI</b>)</li>
                        <li>Generate to view results here; download exports an Excel sheet per key</li>
                        <li>Use <b>Exclude Statuses</b> to filter out records in terminal states</li>
                    </ul>

                    <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }}>Keys</label>
                    <CreatableMultiSelect
                        value={selectedKeys}
                        onChange={(values) => setSelectedKeys(values)}
                        options={availableKeys}
                        placeholder='Select or type keys...'
                        disabled={isLoading}
                        autoNumeric={false}
                        size='md'
                        className='react-select-tall'
                    />
                    <label style={{ fontSize: 13, fontWeight: 600, margin: '10px 0 6px', display: 'block' }}>Exclude Statuses</label>
                    <CreatableMultiSelect
                        value={excludeStatuses}
                        onChange={(values) => setExcludeStatuses(values)}
                        options={statusOptions}
                        placeholder='Select statuses to exclude...'
                        disabled={isLoading}
                        autoNumeric={false}
                        size='md'
                        className='react-select-tall'
                    />
                    <p className='text-muted mb-0 mt-1' style={{ fontSize: 11 }}>⛔ = terminal status. Defaults to excluding terminal states.</p>

                    <div className='d-flex align-items-center gap-2 mt-3'>
                        <ButtonBuilder label={result ? 'Regenerate' : 'Generate'} className='btn-primary btn-sm' type='button' useSpinner={isLoading} onClick={handleGenerate} disabled={!canRun || isLoading} />
                        <ButtonBuilder label='Download Excel' className='btn-outline-secondary btn-sm' type='button' useSpinner={false} onClick={handleDownload} disabled={!canRun || isLoading} />
                    </div>

                    {keyResults.length > 0 && (() => {
                        const active = keyResults.find(k => k.key === activeKey) || keyResults[0];
                        return (
                            <div className='mt-3'>
                                {/* One tab per analyzed key — view a single result at a time */}
                                <ul className='nav nav-tabs' style={{ fontSize: 12 }}>
                                    {keyResults.map((k) => (
                                        <li className='nav-item' key={k.key}>
                                            <button type='button'
                                                className={`nav-link ${k.key === active.key ? 'active' : ''}`}
                                                style={{ padding: '4px 10px', fontSize: 12 }}
                                                onClick={() => setActiveKey(k.key)}>
                                                {k.key} <span className='text-muted'>({k.values.length})</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                                <div className='border border-top-0 p-2'>
                                    {active.values.length === 0 ? (
                                        <p className='text-muted mb-0' style={{ fontSize: 12 }}>No values found in staged records.</p>
                                    ) : (
                                        <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                                            <TableBuilder
                                                subHeader={true}
                                                processing={false}
                                                pagination={false}
                                                sortField={null}
                                                styles={stickyHeaderStyles}
                                                columnsToSearch={searchKeysForKey(active)}
                                                data={active.values}
                                                columns={columnsForKey(active)}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            }
        />
    );
};

export default DataReconModal;
